import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

/** Google OAuth landing route — reads `session_id` from the URL fragment,
 *  exchanges it for a Checkmate JWT via the backend, then redirects to the
 *  admin dashboard. Guarded by useRef so React StrictMode's double-mount
 *  can't fire the exchange twice.
 *
 *  REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
export default function AuthCallback() {
  const location = useLocation();
  const nav = useNavigate();
  const { setUser } = useAuth();
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const hash = location.hash || window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    if (!match) {
      toast.error("No session returned — try again.");
      nav("/login");
      return;
    }
    const sid = decodeURIComponent(match[1]);
    (async () => {
      try {
        const { data } = await api.post("/auth/google-session", { session_id: sid });
        if (setUser) setUser({ id: data.id, email: data.email, name: data.name, role: data.role });
        toast.success(`Welcome, ${data.name || data.email}`);
        // Clear the hash before we leave so a browser-back doesn't retrigger.
        window.history.replaceState({}, "", "/app");
        nav("/app");
      } catch (e) {
        toast.error(e?.response?.data?.detail || e.message || "Google sign-in failed");
        nav("/login");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-paper flex items-center justify-center">
      <div className="hard-card p-8 max-w-md text-center">
        <div className="overline mb-2">§ SIGNING YOU IN</div>
        <h1 className="font-heading font-black text-3xl mb-2">Google Handoff</h1>
        <p className="font-mono text-xs text-muted-foreground">Exchanging session token · this should take under a second.</p>
      </div>
    </div>
  );
}
